import { Badge } from "@/components/ui/badge";
import {
  Download,
  FileCheck,
  FileSpreadsheet,
  Lock,
  Upload,
  Zap,
} from "lucide-react";

const features = [
  {
    icon: Upload,
    title: "Drop it and forget the setup",
    description:
      "Drag a text-based PDF bank statement onto the page. Multi-page statements are supported, with no templates to configure first.",
    badge: null,
    wide: true,
  },
  {
    icon: Zap,
    title: "Extract transactions into rows",
    description:
      "Dates, payees, debits, credits, and balances are organized into spreadsheet columns so you can review them before export.",
    badge: "Reviewable",
    wide: false,
  },
  {
    icon: FileSpreadsheet,
    title: "Open it in Excel immediately",
    description:
      "Download a clean .xlsx file with properly formatted columns. Works in Excel, Google Sheets and Numbers. No reformatting required.",
    badge: "Popular",
    wide: false,
  },
  {
    icon: Lock,
    title: "Your PDF is parsed in the browser",
    description:
      "The original statement is read locally for conversion. If you save a result, only the generated spreadsheet is stored in your account.",
    badge: null,
    wide: true,
  },
  {
    icon: FileCheck,
    title: "Built for common bank layouts",
    description:
      "Works best with selectable-text statements that include clear transaction rows. Scanned image PDFs may need OCR first.",
    badge: null,
    wide: false,
  },
  {
    icon: Download,
    title: "Re-download any conversion, any time",
    description:
      "Every conversion is saved to your dashboard. No re-uploading, no re-processing. Just find it and download again.",
    badge: null,
    wide: false,
  },
];

const Features = () => {
  return (
    <section className="bg-[#fbfbfc] px-4 py-20 md:py-24">
      <div className="mx-auto max-w-[1160px] px-[30px]">
        <div className="mb-16 text-center">
          <Badge
            className="mb-4 rounded-full px-4 py-1.5 border border-primary/20 bg-primary/5 text-primary font-medium"
            style={{ background: "hsl(var(--primary) / 0.05)" }}
          >
            Features
          </Badge>
          <h2
            className="mb-4 font-medium"
            style={{
              fontSize: "clamp(1.75rem, 4vw, 2.5rem)",
              letterSpacing: "-1.5px",
              color: "#1e0d01",
            }}
          >
            Everything you need. Nothing you don't.
          </h2>
          <p
            className="mx-auto max-w-2xl text-lg leading-relaxed"
            style={{ color: "rgba(30,13,1,0.6)" }}
          >
            No bloat, no learning curve. Upload your PDF and you're done.
          </p>
        </div>

        {/* Asymmetric bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Row 1: wide (2/3) + narrow (1/3) */}
          <div className="group relative md:col-span-2 rounded-[24px] border border-[#1e0d01]/8 bg-white p-8 transition-all hover:shadow-md hover:border-primary/20">
            {features[0].badge && (
              <Badge className="absolute -right-2 -top-2 rounded-full bg-primary text-white text-xs">
                {features[0].badge}
              </Badge>
            )}
            <div className="mb-4 inline-flex rounded-[12px] bg-primary/10 p-3 transition-transform group-hover:scale-110">
              <Upload className="h-6 w-6 text-primary" />
            </div>
            <h3 className="mb-2 text-xl font-medium" style={{ color: "#1e0d01", letterSpacing: "-0.5px" }}>
              {features[0].title}
            </h3>
            <p className="leading-relaxed" style={{ color: "rgba(30,13,1,0.6)" }}>
              {features[0].description}
            </p>
          </div>

          <div className="group relative rounded-[24px] border border-[#1e0d01]/8 bg-white p-8 transition-all hover:shadow-md hover:border-primary/20">
            {features[1].badge && (
              <Badge className="absolute -right-2 -top-2 rounded-full bg-primary text-white text-xs">
                {features[1].badge}
              </Badge>
            )}
            <div className="mb-4 inline-flex rounded-[12px] bg-primary/10 p-3 transition-transform group-hover:scale-110">
              <Zap className="h-6 w-6 text-primary" />
            </div>
            <h3 className="mb-2 text-xl font-medium" style={{ color: "#1e0d01", letterSpacing: "-0.5px" }}>
              {features[1].title}
            </h3>
            <p className="leading-relaxed" style={{ color: "rgba(30,13,1,0.6)" }}>
              {features[1].description}
            </p>
          </div>

          {/* Row 2: narrow (1/3) + wide (2/3) — zig-zag */}
          <div className="group relative rounded-[24px] border border-[#1e0d01]/8 bg-white p-8 transition-all hover:shadow-md hover:border-primary/20">
            {features[2].badge && (
              <Badge className="absolute -right-2 -top-2 rounded-full bg-primary text-white text-xs">
                {features[2].badge}
              </Badge>
            )}
            <div className="mb-4 inline-flex rounded-[12px] bg-primary/10 p-3 transition-transform group-hover:scale-110">
              <FileSpreadsheet className="h-6 w-6 text-primary" />
            </div>
            <h3 className="mb-2 text-xl font-medium" style={{ color: "#1e0d01", letterSpacing: "-0.5px" }}>
              {features[2].title}
            </h3>
            <p className="leading-relaxed" style={{ color: "rgba(30,13,1,0.6)" }}>
              {features[2].description}
            </p>
          </div>

          <div className="group relative md:col-span-2 rounded-[24px] border border-[#1e0d01]/8 bg-white p-8 transition-all hover:shadow-md hover:border-primary/20">
            <div className="mb-4 inline-flex rounded-[12px] bg-primary/10 p-3 transition-transform group-hover:scale-110">
              <Lock className="h-6 w-6 text-primary" />
            </div>
            <h3 className="mb-2 text-xl font-medium" style={{ color: "#1e0d01", letterSpacing: "-0.5px" }}>
              {features[3].title}
            </h3>
            <p className="leading-relaxed" style={{ color: "rgba(30,13,1,0.6)" }}>
              {features[3].description}
            </p>
          </div>

          {/* Row 3: 2 equal */}
          <div className="group relative rounded-[24px] border border-[#1e0d01]/8 bg-white p-8 transition-all hover:shadow-md hover:border-primary/20">
            <div className="mb-4 inline-flex rounded-[12px] bg-primary/10 p-3 transition-transform group-hover:scale-110">
              <FileCheck className="h-6 w-6 text-primary" />
            </div>
            <h3 className="mb-2 text-xl font-medium" style={{ color: "#1e0d01", letterSpacing: "-0.5px" }}>
              {features[4].title}
            </h3>
            <p className="leading-relaxed" style={{ color: "rgba(30,13,1,0.6)" }}>
              {features[4].description}
            </p>
          </div>

          <div className="group relative md:col-span-2 rounded-[24px] border border-[#1e0d01]/8 bg-white p-8 transition-all hover:shadow-md hover:border-primary/20">
            <div className="mb-4 inline-flex rounded-[12px] bg-primary/10 p-3 transition-transform group-hover:scale-110">
              <Download className="h-6 w-6 text-primary" />
            </div>
            <h3 className="mb-2 text-xl font-medium" style={{ color: "#1e0d01", letterSpacing: "-0.5px" }}>
              {features[5].title}
            </h3>
            <p className="leading-relaxed" style={{ color: "rgba(30,13,1,0.6)" }}>
              {features[5].description}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
